import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { InvitationDialogContent } from '../components/InvitationDialogContent';
import { useInvitationByToken } from '../hooks/useInvitationByToken';
import { useAcceptInvitation, useRejectInvitationByToken } from '../hooks/useInvitationMutations';
import { FeatureErrorBoundary } from '@/core/error-boundaries/public';
import { sentryReporter } from '@/core/observability/public';
import type { InvitationPublicInfo, InvitationView } from '../types';

function toInvitationView(invitation: InvitationPublicInfo): InvitationView {
  return {
    tenantName: invitation.tenantName,
    role: invitation.role,
  };
}

export default function InvitationAcceptPage() {
  const { t } = useTranslation('invitations');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const { data: invitation, isLoading, isError } = useInvitationByToken(token);
  const { mutateAsync: acceptInvitation, isPending: isAccepting } = useAcceptInvitation();
  const { mutateAsync: rejectInvitation, isPending: isRejecting } = useRejectInvitationByToken();
  const { confirm, isOpen: isConfirmOpen, options, handleConfirm, handleCancel } = useConfirm();

  const isExpired = !!invitation && new Date(invitation.expiresAt).getTime() < new Date().getTime();
  const isUsable = !!invitation && invitation.isValid && !isExpired;

  async function handleAccept() {
    try {
      await acceptInvitation(token);
      navigate('/', { replace: true });
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  async function handleReject() {
    const { confirmed } = await confirm({
      title: t('dialog.confirmReject.title'),
      description: t('dialog.confirmReject.description'),
      confirmLabel: t('dialog.confirmReject.confirm'),
      cancelLabel: t('dialog.confirmReject.cancel'),
      variant: 'destructive',
    });
    if (!confirmed) return;
    try {
      await rejectInvitation(token);
      navigate('/', { replace: true });
    } catch {
      // error handled by global handler in bootstrap.ts
    }
  }

  return (
    <FeatureErrorBoundary reporter={sentryReporter}>
      <div
        data-testid="invitations-accept-page"
        className="min-h-screen flex items-center justify-center bg-background px-4"
      >
        <div className="w-full max-w-sm">
          {isLoading && (
            <p
              data-testid="invitations-accept-loading"
              className="text-center text-sm text-muted-foreground"
            >
              {t('acceptPage.loading')}
            </p>
          )}

          {!isLoading && isError && (
            <p
              data-testid="invitations-accept-error"
              className="text-center text-sm text-destructive"
            >
              {t('acceptPage.error')}
            </p>
          )}

          {!isLoading && !isError && !invitation && (
            <p
              data-testid="invitations-accept-not-found"
              className="text-center text-sm text-muted-foreground"
            >
              {t('acceptPage.notFound')}
            </p>
          )}

          {!isLoading && !isError && invitation && !isUsable && (
            <p
              data-testid="invitations-accept-unusable"
              className="text-center text-sm text-muted-foreground"
            >
              {isExpired ? t('acceptPage.expired') : t('acceptPage.invalid')}
            </p>
          )}

          {!isLoading && !isError && invitation && isUsable && (
            <div className="rounded-xl border border-border bg-card shadow-sm p-6">
              <h1 className="text-lg font-semibold mb-6 text-center">{t('dialog.title')}</h1>
              <InvitationDialogContent
                invitation={toInvitationView(invitation)}
                onAccept={handleAccept}
                onReject={handleReject}
                isPending={isAccepting || isRejecting}
              />
            </div>
          )}
        </div>

        {options && (
          <ConfirmDialog
            open={isConfirmOpen}
            onOpenChange={(v) => {
              if (!v) handleCancel();
            }}
            title={options.title}
            description={options.description}
            confirmLabel={options.confirmLabel}
            cancelLabel={options.cancelLabel}
            variant={options.variant}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        )}
      </div>
    </FeatureErrorBoundary>
  );
}
